import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowUpLeft, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { overlay } from 'overlay-kit';

import { deleteHeySynclyPromptLibraryMutationOptions } from '@/entities/hey-syncly';
import { errorHandler } from '@/error-handler';
import useWorkspaceId from '@/hooks/domain/useWorkspaceId';
import { cn } from '@/lib/cn';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui';
import { toast } from '@/stores/pure/ToastStore';

import { openDeletePromptLibraryDialog } from './dialog.delete';
import { PromptLibraryUpdateDialog } from './dialog.update';
import type { PromptLibraryItem } from './types';

type Props = {
  onClick: () => void;
  onDelete?: (item: PromptLibraryItem) => void;
  onDoubleClick?: () => void;
  onEdit?: (item: PromptLibraryItem) => void;
  item: PromptLibraryItem;
  selected?: boolean;
  showActions?: boolean;
};

type ActionMenuProps = {
  item: PromptLibraryItem;
  onDelete: () => void;
  onEdit: () => void;
};

type ItemActionHandlers = {
  onClickDelete: () => Promise<void>;
  onClickEdit: () => void;
};

const PromptLibraryItemActionMenu = ({
  item,
  onDelete,
  onEdit,
}: ActionMenuProps) => {
  if (item.is_default) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <span onClick={(event) => event.stopPropagation()}>
          <Button size="icon-xs" variant="ghost">
            <MoreHorizontal className="size-4" />
          </Button>
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
        >
          <Pencil className="size-4" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          variant="destructive"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 className="size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const usePromptLibraryItemActions = ({
  item,
  onDelete,
  onEdit,
}: {
  item: PromptLibraryItem;
  onDelete?: (item: PromptLibraryItem) => void;
  onEdit?: (item: PromptLibraryItem) => void;
}): ItemActionHandlers => {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const deleteMutation = useMutation(
    deleteHeySynclyPromptLibraryMutationOptions(queryClient),
  );

  const onClickEdit = () => {
    if (onEdit) {
      onEdit(item);
      return;
    }

    overlay.open(({ isOpen, close }) => (
      <PromptLibraryUpdateDialog
        onOpenChange={close}
        open={isOpen}
        workspaceId={workspaceId}
        promptLibraryId={item.id}
        defaultValues={{
          title: item.title,
          prompt: item.prompt,
        }}
      />
    ));
  };

  const onClickDelete = async () => {
    if (onDelete) {
      onDelete(item);
      return;
    }

    await openDeletePromptLibraryDialog({
      promptTitle: item.title,
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync({
            workspace_id: workspaceId,
            prompt_library_id: item.id,
          });

          toast('Prompt deleted', 'success');
          return true;
        } catch (error) {
          toast(errorHandler(error, 'Failed to delete prompt'), 'fail');
          return false;
        }
      },
    });
  };

  return { onClickDelete, onClickEdit };
};

export const PromptLibraryItemRow = ({
  onClick,
  onDelete,
  onEdit,
  item,
  selected = false,
  showActions = false,
}: Props) => {
  const { onClickDelete, onClickEdit } = usePromptLibraryItemActions({
    item,
    onDelete,
    onEdit,
  });

  return (
    <div
      className={cn(
        'group relative flex items-center gap-4 rounded-lg px-4 py-3.5 transition-colors cursor-pointer',
        selected ? 'bg-accent' : 'hover:bg-foreground/[0.02]',
      )}
      onClick={onClick}
      role="button"
    >
      <div className="flex-1 min-w-0 text-left">
        <div className="flex item-center gap-3">
          <p className="text-sm font-medium truncate text-foreground">
            {item.title}
          </p>
          {item.is_default ? <Badge variant="secondary">Template</Badge> : null}
        </div>
        <p className="mt-0.5 truncate text-sm text-muted-foreground">
          {item.prompt}
        </p>
      </div>

      <div
        className={cn(
          'items-center gap-0.5',
          showActions ? 'block' : 'group-hover:block hidden',
        )}
      >
        <ArrowUpLeft className="size-4" />
      </div>

      <PromptLibraryItemActionMenu
        item={item}
        onEdit={() => {
          onClickEdit();
        }}
        onDelete={() => {
          void onClickDelete();
        }}
      />
    </div>
  );
};
